// src/components/BlitzLayout.js
import React, { useState, useMemo } from 'react';
import Navbar from './Navbar/NavBar';
import { useLocation } from 'react-router-dom';
import { 
  Box, 
  Toolbar, 
  IconButton, 
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { drawerWidth } from '../Utils/constants';
import useIsDesktop from '../Hooks/useIsDesktop';
import BlitzHeader from './BlitzHeader';
import KeyboardArrowLeftSharpIcon from '@mui/icons-material/KeyboardArrowLeftSharp';
import KeyboardArrowRightSharpIcon from '@mui/icons-material/KeyboardArrowRightSharp';
import { lightTheme } from '../Utils/baseTheme';
import UnifiedNotificationCenter from './tools/UnifiedNotificationCenter';

const openedMixin = (theme) => ({
  minWidth: {
    xs: '100%',
    md: `calc(100% - ${drawerWidth})`,
  },
  marginInlineStart: {
    xs: 0,
    md: drawerWidth,
  },
  transition: theme.transitions.create(['min-width', 'margin-inline-start'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create(['min-width', 'margin-inline-start'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  minWidth: {
    xs: '100%',
    md: `calc(100% - ${lightTheme.spacing(8)} + 1px)`,
  },
  marginInlineStart: {
    xs: 0,
    md: `calc(${lightTheme.spacing(8)} - 1px)`,
  },
});

const BlitzLayout = ({ children, containerStyle = {}, onHelp }) => {
  const isDesktop = useIsDesktop();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(isDesktop);
  const [mobileActions, setMobileActions] = useState([]);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const isPartnershipDetailRoute = useMemo(
    () =>
      /^\/partnerships\/[^/]+\/?$/.test(location.pathname) ||
      /^\/creatorconnect\/partnerships\/[^/]+\/?$/.test(location.pathname) ||
      /^\/agency\/creators\/[^/]+\/collaborations\/partnerships\/[^/]+\/?$/.test(location.pathname),
    [location.pathname]
  );
  const hideMobileShell = !isDesktop && isPartnershipDetailRoute;
  const isExtended = !isDesktop || isOpen;

  const handleGetMobileActions = (actions) => setMobileActions(actions);
  const handleCloseNotifications = () => setNotificationAnchor(null);

  // Menu button - desktop gets toggle arrows, mobile gets nothing
  const MenuButton = isDesktop ? (
    <IconButton
      edge="start"
      onClick={() => setIsOpen(prev => !prev)}
      sx={{ color: '#fff', mr: 2 }}
    >
      {isOpen ? (
        <KeyboardArrowLeftSharpIcon />
      ) : (
        <KeyboardArrowRightSharpIcon />
      )}
    </IconButton>
  ) : null;

  const containerStylesWithBanner = useMemo(() => ({
    ...containerStyle,
    minWidth: {
      xs: '100%',
      md: isExtended ? `calc(100% - ${drawerWidth})` : `calc(100% - ${lightTheme.spacing(8)} + 1px)`,
    },
    marginInlineStart: {
      xs: 0,
      md: isExtended ? drawerWidth : `calc(${lightTheme.spacing(8)} - 1px)`,
    },
    transition: lightTheme.transitions.create(['min-width', 'margin-inline-start'], {
      easing: lightTheme.transitions.easing.sharp,
      duration: isExtended 
        ? lightTheme.transitions.duration.enteringScreen 
        : lightTheme.transitions.duration.leavingScreen,
    }),
  }), [isExtended, containerStyle]);

  return (
    <>
      <ThemeProvider theme={lightTheme}>
        {!hideMobileShell && (
          <Navbar
            isDesktop={isDesktop}
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            onClickHelp={onHelp}
            onOpen={() => setIsOpen(true)}
            onGetMobileActions={handleGetMobileActions}
            notificationAnchor={notificationAnchor}
            onSetNotificationAnchor={setNotificationAnchor}
          />
        )}
        
        {/* Show header ONLY on mobile */}
        {!hideMobileShell && !isDesktop && (
          <>
            <BlitzHeader
              menuButton={MenuButton}
              isDesktop={isDesktop}
              mobileActions={mobileActions}
            />
            <Toolbar />
          </>
        )}

        {/* UNIFIED NOTIFICATION CENTER - Works for both desktop and mobile */}
        {!hideMobileShell && (
          <UnifiedNotificationCenter
            anchorEl={notificationAnchor}
            onClose={handleCloseNotifications}
            isCreator={false}
          />
        )}
      </ThemeProvider>
      
      <Box
        id='TopPageContainer'
        sx={containerStylesWithBanner}
      >
        {children}
      </Box>
    </>
  );
};

export default BlitzLayout;
